"use client";

import { useState, useTransition } from "react";
import { login } from "../actions";
import { useRouter } from "next/navigation";
import { LogIn, Mail, Lock, AlertCircle, ArrowRight, Sparkles } from "lucide-react";
import Link from "next/link";

export default function LoginPage() {
    const [isPending, startTransition] = useTransition();
    const [error, setError] = useState<string | null>(null);
    const router = useRouter();

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        const formData = new FormData(e.currentTarget);

        startTransition(async () => {
            const res = await login(formData);
            if (res.success) {
                router.push("/");
                router.refresh();
            } else {
                setError(res.error || "Failed to login");
            }
        });
    };

    // Check for errors in search params (Generic Provider Error Handling)
    if (typeof window !== "undefined") {
        const urlParams = new URLSearchParams(window.location.search);
        const urlError = urlParams.get("error");
        const provider = urlParams.get("provider");
        if (urlError === "provider_error" && !error) {
            const providerName = provider === 'google' ? 'Google' : provider || 'the';
            setError(`${providerName} authentication failed. Please try again or use email login.`);
        }
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
            {/* Ambient Background Glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[120px] animate-pulse"></div>
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>

            <div className="max-w-md w-full relative z-10">
                <div className="text-center mb-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary/10 text-primary mb-4 shadow-inner">
                        <Sparkles className="w-8 h-8" />
                    </div>
                    <h1 className="text-4xl font-black tracking-tight mb-2 bg-gradient-to-br from-foreground to-foreground/60 bg-clip-text text-transparent">
                        Welcome Back
                    </h1>
                    <p className="text-secondary-foreground/60 font-medium italic">Secure entry to your family's financial vault.</p>
                </div>

                <div className="bg-card/50 backdrop-blur-2xl border border-white/10 rounded-[2.5rem] p-8 shadow-2xl shadow-primary/5 animate-in fade-in zoom-in-95 duration-500 relative overflow-hidden">
                    {/* Glass sheen */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent pointer-events-none"></div>

                    <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                        {error && (
                            <div className="bg-danger/10 border border-danger/20 rounded-2xl p-4 flex gap-3 text-danger animate-in shake duration-300">
                                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                <p className="text-sm font-bold">{error}</p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-secondary-foreground/60 ml-1">Email Address</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Mail className="w-5 h-5 text-secondary-foreground group-focus-within:text-primary transition-colors" />
                                </div>
                                <input
                                    type="email"
                                    name="email"
                                    required
                                    placeholder="name@example.com"
                                    className="w-full bg-secondary/30 border-2 border-transparent focus:border-primary/20 rounded-2xl py-4 pl-12 pr-4 text-lg font-bold transition-all focus:outline-none focus:ring-4 focus:ring-primary/5 shadow-inner"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold uppercase tracking-widest text-secondary-foreground/60 ml-1">Password</label>
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <Lock className="w-5 h-5 text-secondary-foreground group-focus-within:text-primary transition-colors" />
                                </div>
                                <input
                                    type="password"
                                    name="password"
                                    required
                                    placeholder="••••••••"
                                    className="w-full bg-secondary/30 border-2 border-transparent focus:border-primary/20 rounded-2xl py-4 pl-12 pr-4 text-lg font-bold transition-all focus:outline-none focus:ring-4 focus:ring-primary/5 shadow-inner"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={isPending}
                            className="w-full py-4 bg-primary text-primary-foreground rounded-2xl font-black text-lg shadow-xl shadow-primary/20 hover:shadow-primary/30 hover:-translate-y-1 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 group"
                        >
                            {isPending ? "Unlocking..." : "Unlock Vault"}
                            {!isPending && <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />}
                        </button>

                        <div className="relative py-4">
                            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                            <div className="relative flex justify-center text-xs uppercase"><span className="bg-card px-2 text-secondary-foreground/40 font-bold tracking-widest">Or continue with</span></div>
                        </div>

                        <Link
                            href="/api/auth/google"
                            className="w-full py-4 bg-white text-gray-900 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all active:scale-95 flex items-center justify-center gap-3 group border border-gray-200"
                        >
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="#EA4335" d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z" />
                                <path fill="#34A853" d="M16.04 18.013c-1.09.303-2.204.496-3.386.496-3.132 0-5.83-1.782-7.143-4.394L1.442 17.29C3.473 21.368 7.502 24 12 24c3.086 0 5.86-1.114 8.014-3L16.04 18.013Z" />
                                <path fill="#4285F4" d="M23.49 12.275c0-.85-.078-1.636-.213-2.39H12v4.544h6.463c-.278 1.481-1.12 2.731-2.38 3.58l3.974 3.086C22.378 18.995 24 15.82 23.49 12.275Z" />
                                <path fill="#FBBC05" d="M5.51 14.115c-.213-.637-.336-1.32-.336-2.035s.123-1.398.336-2.035L1.484 6.93C.542 8.441 0 10.151 0 12c0 1.849.542 3.559 1.484 5.07l4.026-2.955Z" />
                            </svg>
                            Google Account
                        </Link>
                    </form>
                </div>

                <p className="text-center mt-8 text-secondary-foreground animate-in fade-in slide-in-from-top-2 duration-1000 delay-500">
                    New here?{" "}
                    <Link href="/register" className="text-primary font-bold hover:underline decoration-2 underline-offset-4">
                        Create an account
                    </Link>
                </p>
            </div>
        </div>
    );
}
